/**
 * Room-number generation for the client master (FRD §3.1).
 *
 * A B2B client's rooms are defined once at setup and reused every cycle, so
 * this runs on the create/edit form and (in build phase B2) server-side on
 * client creation. Keep the two implementations behaviourally identical.
 */

export interface RoomRange {
  id: string
  /** Optional wing/block label, e.g. "A". */
  block: string
  /** Optional floor prefix: floor "1" + room 4 → "104". Blank = no prefix. */
  floor: string
  from: string
  to: string
}

export interface GeneratedRoom {
  roomNumber: string
  block: string | null
  floor: number | null
}

export interface RoomPatternResult {
  rooms: GeneratedRoom[]
  /** Room numbers that appear more than once across all ranges. */
  duplicates: string[]
  /** Human-readable problem with the pattern itself, if any. */
  error: string | null
}

/** Hard ceiling so a typo (1–100000) cannot lock up the browser. */
export const MAX_ROOMS = 2000

export const emptyRange = (id: string): RoomRange => ({
  id,
  block: '',
  floor: '',
  from: '',
  to: '',
})

const pad = (value: number) => String(value).padStart(2, '0')

const buildNumber = (floor: string, value: number) =>
  floor.trim() ? `${floor.trim()}${pad(value)}` : String(value)

const collectDuplicates = (rooms: GeneratedRoom[]): string[] => {
  const seen = new Set<string>()
  const duplicates: string[] = []

  rooms.forEach((room) => {
    if (seen.has(room.roomNumber)) {
      if (!duplicates.includes(room.roomNumber)) duplicates.push(room.roomNumber)
      return
    }
    seen.add(room.roomNumber)
  })

  return duplicates
}

export const generateRoomsFromRanges = (ranges: RoomRange[]): RoomPatternResult => {
  const rooms: GeneratedRoom[] = []

  for (const range of ranges) {
    const from = Number(range.from)
    const to = Number(range.to)

    // A half-filled row is "not ready yet", not an error - the operator is still typing.
    if (!range.from.trim() && !range.to.trim()) continue

    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      return { rooms: [], duplicates: [], error: 'Room numbers must be numeric.' }
    }

    if (from < 0 || to < 0) {
      return { rooms: [], duplicates: [], error: 'Room numbers cannot be negative.' }
    }

    if (to < from) {
      return { rooms: [], duplicates: [], error: `Range ${from}–${to} ends before it starts.` }
    }

    if (rooms.length + (to - from + 1) > MAX_ROOMS) {
      return {
        rooms: [],
        duplicates: [],
        error: `That pattern makes more than ${MAX_ROOMS} rooms. Split it into a separate client.`,
      }
    }

    for (let value = from; value <= to; value += 1) {
      rooms.push({
        roomNumber: buildNumber(range.floor, value),
        block: range.block.trim() || null,
        floor: range.floor.trim() ? Number(range.floor) : null,
      })
    }
  }

  return { rooms, duplicates: collectDuplicates(rooms), error: null }
}

export const parseCustomRoomList = (text: string, block = ''): RoomPatternResult => {
  const tokens = text
    .split(/[,\n\r\t]+/)
    .map((token) => token.trim())
    .filter(Boolean)

  if (tokens.length > MAX_ROOMS) {
    return {
      rooms: [],
      duplicates: [],
      error: `That list has more than ${MAX_ROOMS} rooms. Split it into a separate client.`,
    }
  }

  const rooms: GeneratedRoom[] = tokens.map((token) => ({
    roomNumber: token,
    block: block.trim() || null,
    floor: null,
  }))

  return { rooms, duplicates: collectDuplicates(rooms), error: null }
}

/** "101–120, 201–220, 301–320" - compresses consecutive numeric runs for the preview. */
export const summariseRooms = (rooms: GeneratedRoom[], maxRuns = 4): string => {
  if (rooms.length === 0) return ''

  const runs: Array<[string, string]> = []
  let startRoom = rooms[0].roomNumber
  let previous = rooms[0].roomNumber

  for (let index = 1; index <= rooms.length; index += 1) {
    const current = rooms[index]?.roomNumber
    const isConsecutive = current !== undefined && Number(current) === Number(previous) + 1

    if (!isConsecutive) {
      runs.push([startRoom, previous])
      if (current === undefined) break
      startRoom = current
    }

    previous = current as string
  }

  const rendered = runs
    .slice(0, maxRuns)
    .map(([start, end]) => (start === end ? start : `${start}–${end}`))

  return runs.length > maxRuns ? `${rendered.join(', ')} …` : rendered.join(', ')
}
